import { useEffect, useState } from "react";
import { track } from "@/lib/analytics";
import { richTextToPlainText } from "@/lib/rich-text";
import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { CalendarDays, MapPin, Users } from "lucide-react";

import { SiteHeader } from "@/components/site/site-header";
import { SiteFooter } from "@/components/site/site-footer";
import { PublicErrorState } from "@/components/site/public-error";

import { listPublicEvents } from "@/lib/events.functions";

const eventsQuery = queryOptions({
  queryKey: ["events", "public"],
  queryFn: () => listPublicEvents(),
});

const TITLE = "Volunteer Events and Shift Signups | T1D Warriors";
const DESCRIPTION =
  "Claim a shift at an upcoming T1D Warriors event across Northern Kentucky and Greater Cincinnati. Pick a time slot, sign up in a minute, and we handle the rest.";

export function formatDay(value: string) {
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDayShort(value: string) {
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function formatTime(value: string | null) {
  if (!value) return null;
  const [h, m] = value.split(":");
  const hour = Number(h);
  if (!Number.isFinite(hour)) return value;
  const suffix = hour >= 12 ? "PM" : "AM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${(m ?? "00").slice(0, 2)} ${suffix}`;
}

export const Route = createFileRoute("/events/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(eventsQuery),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://volunteer.t1dw.org/events" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: "https://volunteer.t1dw.org/events" }],
  }),
  errorComponent: ({ error }) => (
    <PublicErrorState
      error={error}
      route="/events"
      message="We could not load the events board just now. Refresh and it should come back."
    />
  ),
  component: EventsPage,

});

function EventsPage() {
  const { data: events } = useSuspenseQuery(eventsQuery);
  const [openOnly, setOpenOnly] = useState(false);

  useEffect(() => {
    track("events_index_view");
  }, []);

  const visible = openOnly ? events.filter((event) => event.spotsOpen > 0) : events;
  const withOpen = events.filter((event) => event.spotsOpen > 0).length;




  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="app-light">
      <main
        className="mx-auto max-w-5xl px-4 pb-20 sm:px-6 sm:pb-24"
        style={{ paddingTop: "calc(var(--site-hdr, 72px) + 2.5rem)" }}
      >

        <p className="t-eyebrow">On-site crew</p>
        <h1 className="t-h1 mt-3 text-balance text-ice sm:mt-4">
          Grab a shift at an upcoming event
        </h1>
        <p className="t-body mt-4 max-w-2xl">
          These are the real days we need hands. Pick a slot that fits your schedule, claim it, and
          you are on the roster. We will send the details and a reminder before the event.
        </p>

        {events.length === 0 ? (
          <div className="panel mt-8 p-6 text-center sm:mt-10 sm:p-10">
            <CalendarDays className="mx-auto h-10 w-10 text-cyan" />
            <h2 className="t-h3 mt-5 text-ice">Nothing on the calendar right now</h2>
            <p className="t-body mx-auto mt-3 max-w-md">
              New events go up here as they get scheduled. Apply to join the crew and we will let
              you know when the next one opens.
            </p>
            <Link
              to="/apply"
              search={{ track: "onsite" as const }}
              className="mt-7 inline-flex rounded-sm border border-border px-6 py-3 text-sm font-bold uppercase tracking-wide text-ice transition-colors hover:border-cyan hover:text-cyan"
            >
              Join the crew
            </Link>
          </div>
        ) : (
          <>
            <div className="panel mt-8 flex flex-col gap-3 p-4 sm:mt-10 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <label className="-mx-2 flex min-h-11 cursor-pointer items-center gap-3 rounded-sm px-2 text-[0.9375rem] font-semibold text-foreground">
                <input
                  type="checkbox"
                  checked={openOnly}
                  onChange={(e) => setOpenOnly(e.target.checked)}
                  className="h-5 w-5 shrink-0 accent-cyan"
                />
                Only show events with spots left
              </label>
              <span className="text-sm text-muted-foreground">
                {withOpen} of {events.length} {events.length === 1 ? "event" : "events"} still need
                help
              </span>
            </div>

            {visible.length === 0 ? (
              <p className="t-body mt-8 text-sm">
                Every event is fully claimed right now. Uncheck the filter to see them all.
              </p>
            ) : null}

            <div className="mt-6 grid gap-5 sm:grid-cols-2">

            {visible.map((event) => (

              <Link
                key={event.id}
                to="/events/$slug"
                params={{ slug: event.slug }}
                className="panel group flex flex-col p-5 transition-colors hover:border-cyan/60 active:border-cyan sm:p-6"
              >
                {event.program ? <p className="t-eyebrow">{event.program}</p> : null}
                <h2 className="t-h3 mt-3 text-ice group-hover:text-cyan">{event.title}</h2>
                <p className="mt-3 line-clamp-3 text-[0.9375rem] leading-relaxed text-muted-foreground">
                  {richTextToPlainText(event.description ?? "")}
                </p>
                <dl className="mt-5 space-y-2 text-[0.9375rem] text-foreground">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 shrink-0 text-cyan" />
                    <span>
                      {event.firstDate ? formatDayShort(event.firstDate) : "Dates coming soon"}
                      {event.lastDate && event.lastDate !== event.firstDate
                        ? ` to ${formatDayShort(event.lastDate)}`
                        : ""}
                    </span>
                  </div>
                  {event.location_name ? (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 shrink-0 text-cyan" />
                      <span>{event.location_name}</span>
                    </div>
                  ) : null}
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 shrink-0 text-cyan" />
                    <span>
                      {event.spotsOpen > 0
                        ? `${event.spotsOpen} of ${event.spotsTotal} spots still open`
                        : "All spots filled"}
                    </span>
                  </div>
                </dl>
                <span className="mt-6 text-sm font-bold uppercase tracking-wide text-cyan">
                  View signup sheet
                </span>
              </Link>
            ))}
            </div>
          </>
        )}

      </main>
      </div>
      <SiteFooter />

    </div>
  );
}
